import React, { useCallback, useEffect, useMemo } from 'react';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { NextPage } from 'next';
import { getErrorFromStates, isFetchedStates, isFetchingStates } from 'store/utils';

import { EventHostsActions, EventsActions, UserActions } from 'store/actions';
import { EventHostsSelectors, EventsSelectors, UserSelectors } from 'store/selectors';

import withRouteValidation from 'hocs/withRouteValidation';
import withGetInitialPropsRouteValidation from 'hocs/withGetInitialPropsRouteValidation';
import EventMetaTags from 'components/event/EventMetaTags';
import { UserHeader } from 'components/common/Header';
import { TabTitle } from 'components/common/TabTitle';
import RenderComponent from 'components/common/RenderComponent';
import { RootState } from 'store/rootReducer';
import FavoriteEventsSwiper from 'components/FavoriteEventsSwiper/FavoriteEventsSwiper';
import EventScreenV2 from 'components/screens/EventScreenV2';
import Footer from 'components/home/Footer';
import { useAuthProps } from 'hooks';

interface EventPageProps {
  eventId: number;
}

const querySchema = Yup.object({
  eventId: Yup.number().required(),
});

const EventPage: NextPage<EventPageProps, any> = ({ eventId }) => {
  const dispatch = useDispatch();
  const eventState = useSelector((state: RootState) => EventsSelectors.eventStateById(state, eventId));
  const eventHostsState = useSelector((state: RootState) => EventHostsSelectors.eventHostsState(state, { eventId }));
  const passportsState = useSelector(UserSelectors.userPassportsState);
  const { isAuthenticated, isHardUser, currentUser } = useAuthProps();

  const specialHosts = useMemo(() => {
    if (eventHostsState.entities.length > 8) return eventHostsState.entities.slice(0, 8);
    return eventHostsState.entities;
  }, [eventHostsState.entities]);

  useEffect(() => {
    if (isAuthenticated && isHardUser && currentUser) {
      dispatch(UserActions.getUserPassports.request());
    } else dispatch(UserActions.getUserPassports.reset());
  }, [currentUser, dispatch, isAuthenticated, isHardUser]);

  const handleToggleFavoriteEvent = useCallback(
    (eventId: any) => dispatch(EventsActions.toggleFavoriteEvent.request({ id: eventId })),
    [dispatch],
  );

  return (
    <>
      <UserHeader sticky />
      <RenderComponent
        layout
        isLoading={isFetchingStates(eventState) || passportsState.isFetching}
        errors={getErrorFromStates(eventState)}
        isFetched={isFetchedStates(eventState)}
        renderContent={() => (
          <>
            <TabTitle directTitle={eventState.entity.title} signLabel="general" />
            <EventMetaTags event={eventState.entity} />
            <EventScreenV2
              event={eventState.entity}
              specialHosts={specialHosts}
              onToggleFavoriteEvent={handleToggleFavoriteEvent}
            />
          </>
        )}
      />
      <FavoriteEventsSwiper />
      <Footer />
    </>
  );
};

// todo change to getServerSide and refactor
EventPage.getInitialProps = withGetInitialPropsRouteValidation(querySchema, async ({ store, query }: any) => {
  const eventId = Number(query.eventId);

  if (eventId) {
    store.dispatch(EventsActions.getEvent.request({ id: eventId }));
    store.dispatch(EventHostsActions.getEventHosts.request({ params: { eventId } }));

    return {
      eventId,
    };
  }

  return {};
});

export default withRouteValidation(EventPage);
